import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, CheckCircle, AlertCircle, Lightbulb } from 'lucide-react';
import ImageUploader from '../components/upload/ImageUploader';
import UploadProgress from '../components/upload/UploadProgress';
import UploadLimitWarning from '../components/upload/UploadLimitWarning';
import { useUpload } from '../hooks/useUpload';
import { useAuth } from '../context/AuthContext';
import { useDashboardStats } from '../hooks/useDashboardStats'; //  Import React Query hook
import { ROUTES } from '../lib/constants';

const Upload = () => {
  const navigate = useNavigate();
  const { isPremium } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); 
  const [uploadedDocument, setUploadedDocument] = useState(null);

  // Use your uniform query hook cache tracker
  const { stats, refreshStats } = useDashboardStats();

  // Safely fallback to properties if stats are pending resolution
  const remainingUploads = stats?.remaining_uploads ?? 3;
  const todayUploads = stats?.today_uploads ?? 0;

  const { uploadDocument, uploading, progress, currentStep } = useUpload(remainingUploads);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setUploadStatus(null);
    setUploadedDocument(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadStatus('processing');

    try {
      const result = await uploadDocument(selectedFile);
      
      if (result) {
        setUploadStatus('completed');
        setUploadedDocument(result);
        
        // Globally invalidate React Query cache to reset numbers immediately
        refreshStats();
      } else {
        setUploadStatus('failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('failed');
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadStatus(null);
    setUploadedDocument(null);
  };

  const handleViewDocument = () => {
    if (uploadedDocument) {
      navigate(ROUTES.DOCUMENT.replace(':id', uploadedDocument.id));
    }
  };

  const canUpload = isPremium || remainingUploads > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-brown-50">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        
        {/* Page Title */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
            <div className="p-2 sm:p-2.5 bg-gradient-to-br from-brown-500 to-brown-700 rounded-lg shadow-md">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-cream-50" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-brown-900">
              Upload Document
            </h1>
          </div>
          <p className="text-sm sm:text-base md:text-lg text-brown-600">
            Upload a textbook page and let AI simplify it for you
          </p>
        </div>

        {/* Dynamic Warning Component */}
        <div className="mb-4 sm:mb-6">
          <UploadLimitWarning 
            remainingUploads={remainingUploads}
            todayUploads={todayUploads}
            maxUploads={3}
            onUpgradeClick={() => navigate(ROUTES.PRICING)}
          />
        </div>

        <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-lg border border-cream-300/50 p-4 sm:p-6 md:p-8">
          
          {/* STEP 1: INITIAL UPLOAD STATE */}
          {uploadStatus === null && (
            <div className="space-y-4 sm:space-y-6">
              <ImageUploader 
                onFileSelect={handleFileSelect}
                disabled={!canUpload || uploading}
              />

              {selectedFile && canUpload && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-4 sm:pt-6 border-t border-cream-300/50 gap-3 sm:gap-4">
                  <p className="text-xs sm:text-sm md:text-base text-brown-700">
                    Ready to process your document with AI 
                  </p>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                    <button
                      onClick={handleReset}
                      disabled={uploading}
                      className="w-full sm:w-auto px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 border-2 border-cream-300 text-brown-700 rounded-lg hover:bg-cream-50/50 transition-all font-medium disabled:opacity-50 text-sm sm:text-base"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpload}
                      disabled={uploading}
                      className="w-full sm:w-auto px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-brown-600 to-brown-700 hover:from-brown-700 hover:to-brown-800 text-white rounded-lg transition-all font-medium flex items-center justify-center space-x-2 disabled:opacity-50 shadow-md text-sm sm:text-base"
                    >
                      <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>Process with AI</span>
                    </button>
                  </div>
                </div>
              )}

              {!canUpload && remainingUploads === 0 && (
                <div className="text-center py-4">
                  <p className="text-center text-red-600 text-xs sm:text-sm md:text-base font-medium mb-2">
                    Daily upload limit reached.
                  </p>
                  <button 
                    onClick={() => navigate(ROUTES.PRICING)}
                    className="text-brown-700 font-bold underline hover:text-brown-900 transition-colors">
                    Upgrade to Premium for unlimited access
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* STEP 2: PROCESSING STATE */}
          {uploadStatus === 'processing' && (
            <UploadProgress 
              progress={progress}
              currentStep={currentStep}
              status="processing"
            />
          )}
            
          {/* STEP 3: COMPLETED STATE */}
          {uploadStatus === 'completed' && (
            <div className="text-center py-6 sm:py-8 md:py-12 animate-fade-in">
              <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-2 sm:mb-3">
                Document Processed.
              </h3>
              <p className="text-sm sm:text-base md:text-lg text-brown-600 mb-6 sm:mb-8 px-4">
                Your textbook page has been simplified and is ready to view
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                <button
                  onClick={handleReset}
                  className="w-full sm:w-auto px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 border-2 border-cream-300 text-brown-700 rounded-lg hover:bg-cream-50/50 transition-all font-medium text-sm sm:text-base"
                >
                  Upload Another
                </button>
                <button
                  onClick={handleViewDocument}
                  className="w-full sm:w-auto px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-brown-600 to-brown-700 hover:from-brown-700 hover:to-brown-800 text-white rounded-lg transition-all font-medium shadow-md text-sm sm:text-base"
                >
                  View Summary
                </button>
              </div>
            </div>
          )}
            
          {/* STEP 4: ERROR STATE */}
          {uploadStatus === 'failed' && (
            <div className="text-center py-6 sm:py-8 md:py-12">
              <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                <AlertCircle className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-2 sm:mb-3">
                Processing Failed
              </h3>
              <p className="text-sm sm:text-base md:text-lg text-brown-600 mb-6 sm:mb-8 px-4">
                Something went wrong while processing your document. Please try again.
              </p>
              <button
                onClick={handleReset}
                className="px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-brown-600 to-brown-700 hover:from-brown-700 hover:to-brown-800 text-white rounded-lg transition-all font-medium shadow-md text-sm sm:text-base"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Tips Section */}
        <div className="mt-6 sm:mt-8 backdrop-blur-sm bg-brown-50/50 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-brown-100/50">
          <div className="flex items-start space-x-2 sm:space-x-3 mb-3 sm:mb-4">
            <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-brown-600 flex-shrink-0 mt-0.5" />
            <h4 className="text-sm sm:text-base md:text-lg font-semibold text-brown-900">
               Tips for best results:
            </h4>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs sm:text-sm md:text-base text-brown-800 ml-7 sm:ml-9">
            <li>• Ensure the text is clear and legible</li>
            <li>• Avoid shadows or glare on the page</li>
            <li>• Capture the entire page or section</li>
            <li>• Upload images in JPG, PNG, or WEBP</li>
            <li>• Keep file size under 5MB</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default Upload;